import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Column } from 'primereact/column'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { Image } from 'primereact/image'
import { InputText } from 'primereact/inputtext'
import { SelectButton } from 'primereact/selectbutton'
import { Toolbar } from 'primereact/toolbar'
import { useMemo, useState } from 'react'
import { getCriterionDisplayValue, getCriterionTypeLabel } from '../config/criterion-config'
import { exportAlarmEventsToCSV } from '../utils/export-csv'
import { AlarmOutput } from 'domain/alarm-output'
import { AlarmCriteria } from 'domain/alarm-criteria'
import { SignalRAlarmType } from 'enums/signal-r-alarm-type'
import {
  getAlarmTypeBadgeClass,
  getAlarmTypeIcon,
  getAlarmTypeLabel,
} from '../types/signal-r-alarm-type.helpers'

interface AlarmEventsTableProps {
  alarmEvents: AlarmOutput[]
  totalEvents: number
  isLoading: boolean
  onViewDetails: (event: AlarmOutput) => void
}

type EventTypeFilter = 'all' | 'pessoas' | 'veiculos'

const TYPE_OPTIONS = [
  { label: 'Todos', value: 'all' },
  { label: 'Pessoas', value: 'pessoas' },
  { label: 'Veículos', value: 'veiculos' },
]

const AlarmEventsTable: React.FC<AlarmEventsTableProps> = ({
  alarmEvents,
  totalEvents,
  isLoading,
  onViewDetails,
}) => {
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    value: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })
  const [globalEventFilter, setGlobalEventFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<EventTypeFilter>('all')
  const [dateRange, setDateRange] = useState<Date[] | null>(null)

  const filteredEvents = useMemo(() => {
    return alarmEvents.filter((event) => {
      // Excesso de velocidade é sobre veículo, então entra no filtro
      // "Veículos" junto com ANPR — só AHCR (Pessoas) é o outro lado.
      if (typeFilter === 'pessoas' && event.type !== SignalRAlarmType.AHCR)
        return false
      if (typeFilter === 'veiculos' && event.type === SignalRAlarmType.AHCR)
        return false
      if (dateRange?.[0] && dateRange?.[1]) {
        const date = new Date(event.detectedAt)
        const end = new Date(dateRange[1])
        end.setHours(23, 59, 59, 999)
        if (date < dateRange[0] || date > end) return false
      }
      return true
    })
  }, [alarmEvents, typeFilter, dateRange])

  const hasActiveFilters = typeFilter !== 'all' || dateRange?.[0] != null

  const exportEvents = () => exportAlarmEventsToCSV(filteredEvents)

  const clearFilters = () => {
    setTypeFilter('all')
    setDateRange(null)
    setGlobalEventFilter('')
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.EQUALS },
      value: { value: null, matchMode: FilterMatchMode.CONTAINS },
    })
  }

  const typeBodyTemplate = (rowData: AlarmOutput) => {
    return (
      <span
        className={`alarms-type-badge ${getAlarmTypeBadgeClass(rowData.type)}`}
      >
        <i className={`pi ${getAlarmTypeIcon(rowData.type)}`} />
        {getAlarmTypeLabel(rowData.type)}
      </span>
    )
  }

  const cameraBodyTemplate = (rowData: AlarmOutput) => {
    return (
      <span className='alarms-camera-cell'>
        <i className='pi pi-video' />
        {rowData.cameraName || 'Câmera desconhecida'}
      </span>
    )
  }

  const alarmDateBodyTemplate = (rowData: AlarmOutput) => {
    const date = new Date(rowData.detectedAt)
    return (
      <div className='alarms-alarm-date-cell'>
        <div className='alarms-date-main'>
          <i className='pi pi-exclamation-triangle alarms-alarm-icon' />
          <span>{date.toLocaleDateString('pt-BR')}</span>
        </div>
        <div className='alarms-time-sub'>
          <i className='pi pi-clock' />
          <span>
            {date.toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>
    )
  }

  const imageBodyTemplate = (rowData: AlarmOutput) => {
    return (
      <div className='alarms-image-cell'>
        <div
          className='alarms-image-container'
          onClick={() => onViewDetails(rowData)}
        >
          {rowData.evidence ? (
            <Image
              src={rowData.evidence}
              alt='Imagem do Alarme'
              width='140px'
              height='90px'
              className='alarms-thumbnail'
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                const container = target.parentElement
                if (container) {
                  const placeholder = document.createElement('div')
                  placeholder.className = 'alarms-image-placeholder'
                  placeholder.innerHTML =
                    '<i class="pi pi-image"></i><span>Erro ao carregar</span>'
                  container.appendChild(placeholder)
                }
              }}
            />
          ) : (
            <div className='alarms-image-placeholder'>
              <i className='pi pi-image' />
              <span>Sem imagem</span>
            </div>
          )}
          <div className='alarms-image-overlay'>
            <i className='pi pi-search-plus' />
          </div>
        </div>
      </div>
    )
  }

  const criteriaBodyTemplate = (rowData: AlarmOutput) => {
    if (!rowData.criterias?.length) {
      return (
        <span className='alarm-no-criteria'>
          Nenhum critério
        </span>
      )
    }

    return (
      <div className='alarm-criteria-container'>
        {rowData.criterias.map((criteria: AlarmCriteria) => {
          const label = getCriterionTypeLabel(criteria.type)
          return (
            <div
              key={`${criteria.type}-${criteria.value}`}
              className='alarm-criterion-badge'
            >
              <span className='alarm-criterion-label'>
                {label}
              </span>
              <span className='alarm-criterion-value'>
                {getCriterionDisplayValue(criteria.value)}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  const eventActionBodyTemplate = (rowData: AlarmOutput) => {
    return (
      <div className='alarms-actions'>
        <Button
          icon='pi pi-eye'
          rounded
          className='alarms-action-view'
          onClick={() => onViewDetails(rowData)}
          tooltip='Ver Detalhes'
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    )
  }

  const eventToolbar = (
    <div className='alarms-toolbar-content'>
      <div className='alarms-toolbar-main'>
        <div className='alarms-toolbar-left'>
          <h4 className='alarms-toolbar-title'>
            <i className='pi pi-history' />
            Histórico de Alarmes
          </h4>
        </div>
        <div className='alarms-toolbar-right ml-2'>
          <div className='alarms-search-group'>
            <span className='alarms-search-icon'>
              <i className='pi pi-search' />
            </span>
            <InputText
              value={globalEventFilter}
              onChange={(e) => {
                setGlobalEventFilter(e.target.value)
                let _filters = { ...filters }
                // @ts-ignore
                _filters['global'].value = e.target.value
                setFilters(_filters)
              }}
              placeholder='Pesquisar eventos...'
              className='alarms-search-input'
            />
          </div>
          <div className='alarms-stats'>
            <span className='alarms-badge alarms-badge-events'>
              {hasActiveFilters ? filteredEvents.length : totalEvents}
            </span>
            <span className='alarms-stats-text'>
              {hasActiveFilters ? `de ${totalEvents}` : 'Eventos'}
            </span>
          </div>
        </div>
      </div>
      <div className='alarms-filters-row'>
        <SelectButton
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.value ?? 'all')}
          options={TYPE_OPTIONS}
          className='alarms-type-filter'
        />
        <Calendar
          value={dateRange as Date[]}
          onChange={(e) => setDateRange(e.value as Date[] | null)}
          selectionMode='range'
          readOnlyInput
          placeholder='Filtrar por período...'
          dateFormat='dd/mm/yy'
          showButtonBar
          showIcon
          className='alarms-date-filter'
        />
        {hasActiveFilters && (
          <Button
            icon='pi pi-times'
            label='Limpar'
            outlined
            size='small'
            className='alarms-clear-filters'
            onClick={clearFilters}
          />
        )}
        <Button
          icon='pi pi-file-export'
          label={`Exportar (${filteredEvents.length})`}
          outlined
          size='small'
          className='alarms-export-button'
          onClick={exportEvents}
          disabled={filteredEvents.length === 0}
        />
      </div>
    </div>
  )

  return (
    <>
      <Toolbar
        className='alarms-toolbar'
        start={eventToolbar}
      />
      <DataTable
        value={filteredEvents}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25]}
        dataKey='id'
        responsiveLayout='scroll'
        filters={filters}
        globalFilterFields={['cameraName']}
        emptyMessage='Nenhum evento de alarme encontrado.'
        className='alarms-datatable alarms-events-table'
        stripedRows
        showGridlines
        loading={isLoading}
      >
        <Column
          field='detectedAt'
          header='Data do Alarme'
          body={alarmDateBodyTemplate}
          sortable
          style={{ minWidth: '14rem' }}
          className='alarms-alarm-date-column'
        />
        <Column
          field='type'
          header='Tipo'
          body={typeBodyTemplate}
          sortable
          style={{ minWidth: '10rem' }}
          className='alarms-type-column'
        />
        <Column
          field='cameraName'
          header='Câmera'
          body={cameraBodyTemplate}
          sortable
          style={{ minWidth: '10rem' }}
          className='alarms-camera-column'
        />
        <Column
          header='Critérios'
          body={criteriaBodyTemplate}
          style={{ minWidth: '14rem' }}
          className='alarms-criteria-column'
        />
        <Column
          field='evidence'
          header='Evidência'
          body={imageBodyTemplate}
          style={{ minWidth: '10rem' }}
          className='alarms-image-column'
        />
        <Column
          body={eventActionBodyTemplate}
          header='Ações'
          exportable={false}
          style={{ minWidth: '8rem' }}
          className='alarms-actions-column'
        />
      </DataTable>
    </>
  )
}

export default AlarmEventsTable
